/**
 * Every number that shapes how the mix sounds. Defaults follow PRD §3.4–3.7.
 * They can be adjusted live from the tuning panel (add ?tune to the room URL) and are saved per browser,
 * so the quizmaster can calibrate during a test call and then hard-code the winners here.
 */
export interface AudioTuning {
  /** Public voices and the quizmaster's normal voice while your team is whispering. */
  duckedGain: number;
  /** Team whispers while the quizmaster is announcing. */
  announceTeamGain: number;
  /** Public voices while the quizmaster is announcing. */
  announcePublicGain: number;
  fadeDownMs: number;
  fadeUpMs: number;
  /** How long after the last whispered sound before public audio comes back. */
  holdMs: number;
  /** Level (dBFS) a whisper must exceed to count as speech. */
  vadThresholdDb: number;
  /** Sound must stay above the threshold this long to count (ignores clicks and taps). */
  vadAttackMs: number;
}

export const DEFAULT_TUNING: AudioTuning = {
  duckedGain: 0.35,
  announceTeamGain: 0.33,
  announcePublicGain: 0.35,
  fadeDownMs: 200,
  fadeUpMs: 400,
  holdMs: 1200,
  vadThresholdDb: -50,
  vadAttackMs: 20,
};

export const TUNING_RANGES: Record<keyof AudioTuning, { min: number; max: number; step: number; label: string }> = {
  duckedGain: { min: 0, max: 1, step: 0.01, label: 'Public volume while your team whispers' },
  announceTeamGain: { min: 0, max: 1, step: 0.01, label: 'Whisper volume during announcements' },
  announcePublicGain: { min: 0, max: 1, step: 0.01, label: 'Public volume during announcements' },
  fadeDownMs: { min: 20, max: 1000, step: 10, label: 'Fade down (ms)' },
  fadeUpMs: { min: 20, max: 2000, step: 10, label: 'Fade up (ms)' },
  holdMs: { min: 200, max: 4000, step: 50, label: 'Silence before fading back (ms)' },
  vadThresholdDb: { min: -80, max: -20, step: 1, label: 'Whisper detection threshold (dBFS)' },
  vadAttackMs: { min: 0, max: 200, step: 5, label: 'Ignore sounds shorter than (ms)' },
};

const KEY = 'elixir.tuning';

export function loadTuning(): AudioTuning {
  try {
    return { ...DEFAULT_TUNING, ...JSON.parse(localStorage.getItem(KEY) ?? '{}') };
  } catch {
    return { ...DEFAULT_TUNING };
  }
}

export function saveTuning(t: AudioTuning) {
  try {
    localStorage.setItem(KEY, JSON.stringify(t));
  } catch {
    /* storage unavailable: tuning lasts for this session only */
  }
}
